import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Character } from '@/types';

// ============================================
// AUTH STORE
// ============================================

interface AuthState {
  token: string | null;
  user: User | null;
  character: Character | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  setAuth: (token: string, user: User, character: Character | null) => void;
  setCharacter: (character: Character) => void;
  setUser: (user: User) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      character: null,
      isAuthenticated: false,
      isLoading: true,

      setAuth: (token, user, character) => {
        localStorage.setItem('auth_token', token);
        set({
          token,
          user,
          character,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      setCharacter: (character) => {
        set({ character });
      },

      setUser: (user) => {
        set({ user });
      },

      logout: () => {
        localStorage.removeItem('auth_token');
        set({
          token: null,
          user: null,
          character: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'dungeon-world-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        character: state.character,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// ============================================
// UI STORE
// ============================================

interface UIState {
  isMenuOpen: boolean;
  currentRoute: string;

  setMenuOpen: (open: boolean) => void;
  setCurrentRoute: (route: string) => void;
}

export const useUIStore = create<UIState>((set) => ({
  isMenuOpen: false,
  currentRoute: '/',

  setMenuOpen: (open) => set({ isMenuOpen: open }),
  setCurrentRoute: (route) => set({ currentRoute: route }),
}));

// ============================================
// GAME SESSION STORE
// ============================================

interface GameSessionState {
  sessionId: string | null;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  isLoading: boolean;

  setSessionId: (id: string) => void;
  addMessage: (role: 'user' | 'assistant', content: string) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

export const useGameSessionStore = create<GameSessionState>((set) => ({
  sessionId: null,
  messages: [],
  isLoading: false,

  setSessionId: (id) => set({ sessionId: id }),

  addMessage: (role, content) =>
    set((state) => ({
      messages: [...state.messages, { role, content }],
    })),

  setLoading: (loading) => set({ isLoading: loading }),

  reset: () => set({
    sessionId: null,
    messages: [],
    isLoading: false,
  }),
}));
